// Temporary backfill panel — two historical backlogs that predate the
// dispatch hard gates. Empties once, then stays empty (new occurrences are
// blocked at dispatch). Remove this page when both lists hit zero for good.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminCleanupApi, orderApi, productMappingApi } from '../api/services';
import toast from 'react-hot-toast';
import {
  Loader2, CheckCircle2, Repeat, PackageSearch, ShieldCheck, Search, Plus,
} from 'lucide-react';

const REASON_OPTIONS = [
  ['customs_hold', 'Stuck in customs (50%)'],
  ['lost', 'Lost in transit (50%)'],
  ['damaged', 'Damaged (50%)'],
  ['missing_pills', 'Missing pills (50%)'],
  ['wrong_product', 'Wrong product (vendor decides)'],
  ['customer_feedback', 'Customer dissatisfied (100%)'],
  ['delivered_want_another', 'Delivered — wants another (100%)'],
  ['delivered_not_received', 'Delivered, customer says not received (100%)'],
  ['other', 'Other (100%)'],
];

function ReshipSection() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(null);

  async function load() {
    try {
      const res = await adminCleanupApi.reshipsMissingReason();
      setRows(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load reships');
    }
  }
  useEffect(() => { load(); }, []);

  async function setReason(row, reason) {
    if (!reason) return;
    setSaving(row.id);
    try {
      await orderApi.setReshipReason(row.id, reason);
      toast.success(`${row.order_id_external} → ${reason.replace(/_/g, ' ')} — payables repriced`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(null); }
  }

  if (rows === null) return <div className="py-6 text-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
        <Repeat className="w-4 h-4 text-violet-600" />
        Reships missing a reason
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${rows.length ? 'bg-violet-100 text-violet-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {rows.length}
        </span>
      </h2>
      <p className="text-xs text-gray-500 mb-3">
        These reships were sent to the vendor before reasons became mandatory. They
        currently bill at <b>100%</b> — if any were lost / damaged / customs / missing-pills
        cases, the vendor owes us 50%. Pick the reason and the bill reprices instantly.
      </p>
      {rows.length === 0 ? (
        <div className="py-6 text-center text-sm text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> All reships have a reason. Nothing to do here.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Order</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Sent to vendor</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Why was it reshipped?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">
                  <Link to={`/orders/${r.id}`} className="font-mono text-xs text-blue-700 hover:underline">
                    {r.order_id_external}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs">{r.customer_name}</td>
                <td className="px-3 py-2 text-xs">{r.sent_on ? String(r.sent_on).slice(0, 10) : 'not yet sent'}</td>
                <td className="px-3 py-2 text-xs capitalize">{String(r.lifecycle_stage).replace(/_/g, ' ')}</td>
                <td className="px-3 py-2">
                  <select defaultValue="" disabled={saving === r.id}
                          onChange={(e) => setReason(r, e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-xs w-64">
                    <option value="" disabled>pick a reason…</option>
                    {REASON_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MapRow({ group, onDone }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  async function doSearch(s) {
    setSearch(s);
    if (!s || s.length < 2) { setResults([]); return; }
    try {
      const res = await productMappingApi.list({ search: s, limit: 8 });
      setResults(res.data.mappings || []);
    } catch { /* non-blocking */ }
  }

  async function mapTo({ masterId, createNew }) {
    setBusy(true);
    try {
      const res = await adminCleanupApi.mapProduct({
        indianBrand: group.indian_brand,
        productName: group.product_name,
        masterId,
        createNew,
      });
      toast.success(`Mapped — ${res.data.linesBound} line(s) linked`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Mapping failed');
    } finally { setBusy(false); }
  }

  return (
    <>
      <tr className={open ? 'bg-blue-50/40' : ''}>
        <td className="px-3 py-2 text-xs font-medium">{group.product_name || '(no name)'}</td>
        <td className="px-3 py-2 text-xs text-gray-600">{group.indian_brand || '—'}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{group.line_count}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{group.total_qty}</td>
        <td className="px-3 py-2 text-xs text-gray-500 max-w-[14rem] truncate" title={(group.order_ids || []).join(', ')}>
          {(group.order_ids || []).slice(0, 3).join(', ')}{(group.order_ids || []).length > 3 && ` +${group.order_ids.length - 3}`}
        </td>
        <td className="px-3 py-2 text-right">
          <button onClick={() => setOpen((v) => !v)}
                  className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">
            {open ? 'close' : 'Map…'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-blue-50/40">
          <td colSpan={6} className="px-3 pb-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={search} onChange={(e) => doSearch(e.target.value)}
                       placeholder="Search the product master…"
                       className="pl-7 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs w-72" />
              </div>
              <span className="text-gray-400">or</span>
              <button onClick={() => mapTo({ createNew: { indianBrand: group.indian_brand || group.product_name, genericName: group.product_name } })}
                      disabled={busy}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                <Plus className="w-3.5 h-3.5" /> Create "{group.product_name}" in master & map
              </button>
            </div>
            {results.length > 0 && (
              <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white">
                {results.map((m) => (
                  <li key={m.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
                    <span>
                      <b>{m.indian_brand}</b> / {m.generic_name}
                      {m.container && <span className="text-gray-500"> · {m.container}</span>}
                    </span>
                    <button onClick={() => mapTo({ masterId: m.id })} disabled={busy}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-[11px] font-medium hover:bg-blue-700 disabled:opacity-50">
                      Map to this
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function UnmappedSection() {
  const [groups, setGroups] = useState(null);

  async function load() {
    try {
      const res = await adminCleanupApi.unmappedLines();
      setGroups(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load unmapped lines');
    }
  }
  useEffect(() => { load(); }, []);

  if (groups === null) return <div className="py-6 text-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  const totalLines = groups.reduce((a, g) => a + g.line_count, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
        <PackageSearch className="w-4 h-4 text-amber-600" />
        Order lines not mapped to the product master
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${totalLines ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {totalLines} line(s) · {groups.length} product(s)
        </span>
      </h2>
      <p className="text-xs text-gray-500 mb-3">
        These slipped through before the dispatch gate existed. Until mapped they bill
        ₹0 in Vendor Payables. Map each product once — every line of that product links
        in one go. "Create in master & map" adds it to the master list with this exact
        spelling, so future orders match automatically.
      </p>
      {groups.length === 0 ? (
        <div className="py-6 text-center text-sm text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> Every order line is mapped. Nothing to do here.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Product (from MBO)</th>
              <th className="text-left px-3 py-2">Brand</th>
              <th className="text-right px-3 py-2">Lines</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-left px-3 py-2">Orders</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groups.map((g) => (
              <MapRow key={`${g.indian_brand}|${g.product_name}`} group={g} onDone={load} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DuplicatesSection() {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const res = await adminCleanupApi.duplicateVendorProducts();
      setGroups(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load duplicates');
    }
  }
  useEffect(() => { load(); }, []);

  async function merge(body) {
    setBusy(true);
    try {
      const res = await adminCleanupApi.mergeVendorProducts(body);
      toast.success(`Merged ${res.data.groupsMerged} group(s) — ${res.data.shellsDisabled} duplicate(s) retired, ${res.data.pricesMoved} price window(s) preserved`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Merge failed');
    } finally { setBusy(false); }
  }

  async function split(g) {
    setBusy(true);
    try {
      const res = await adminCleanupApi.splitVendorProducts({ vendorId: g.vendor_id, masterId: g.product_mapping_id });
      toast.success(`Split: ${res.data.split} brand(s) got their own product (each keeps its own price); ${res.data.kept} stayed on ${g.indian_brand}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Split failed');
    } finally { setBusy(false); }
  }

  if (groups === null) return <div className="py-6 text-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Repeat className="w-4 h-4 text-blue-600" />
          Duplicate vendor catalog entries
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${groups.length ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
            {groups.length} group(s)
          </span>
        </h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Several vendor catalog entries point at the same master product. Decide per
        group: <b className="text-blue-700">Merge</b> when they're the <i>same product
        spelled differently</i> (e.g. "CENFORCE 100MG TAB" + "Cenforce 100mg") — one
        entry survives, all price history is kept. <b className="text-violet-700">Split</b>{' '}
        when they're <i>genuinely different brands</i> (e.g. CENFORCE vs BULL FORCE vs
        HIFORCE) — each brand gets its own product and <b>keeps its own price</b>.
        Price uploads now attach renamed products to existing entries automatically,
        so this list shouldn't grow again.
      </p>
      {groups.length === 0 ? (
        <div className="py-6 text-center text-sm text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> No duplicate catalog entries.
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Master product</th>
                <th className="text-left px-3 py-2">Vendor spellings</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groups.map((g) => (
                <tr key={`${g.vendor_id}|${g.product_mapping_id}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-gray-900">{g.indian_brand}</div>
                    <div className="text-gray-500">{g.generic_name} · {g.vendor_name}</div>
                  </td>
                  <td className="px-3 py-2">
                    <ul className="space-y-0.5">
                      {g.shells.map((s) => (
                        <li key={s.id} className="flex items-center gap-2">
                          <span className={s.available ? 'text-gray-800' : 'text-gray-400 line-through'}>{s.name}</span>
                          {s.currentPrice != null
                            ? <span className="text-emerald-700 font-medium">₹{Number(s.currentPrice).toLocaleString('en-IN')}</span>
                            : <span className="text-gray-400">no current price</span>}
                          <span className="text-gray-400">({s.prices} price row(s))</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-2 text-right align-top whitespace-nowrap">
                    <button onClick={() => merge({ vendorId: g.vendor_id, masterId: g.product_mapping_id })}
                            disabled={busy}
                            title="Same product, different spellings — keep one entry, preserve all price history"
                            className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                      Merge
                    </button>
                    <button onClick={() => split(g)}
                            disabled={busy}
                            title="Different BRANDS wrongly grouped — give each brand its own product so each keeps its own price"
                            className="ml-1 px-2.5 py-1 bg-violet-600 text-white rounded text-xs font-medium hover:bg-violet-700 disabled:opacity-50">
                      Split
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminCleanup() {
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900">Data backfill</h1>
      <p className="text-sm text-gray-500 mb-3">
        One-time cleanup of two historical backlogs. Once both sections show zero,
        this page's job is done.
      </p>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>This can't happen again.</strong> Since 11 June the dispatch run
            hard-blocks: (1) any order with a product not mapped to the master, and
            (2) any reship without a reship reason. Adding a product to the master now
            auto-links all older matching lines, and the reship file sent to the vendor
            prints the reason in plain words. Everything below is old data only.
          </div>
        </div>
      </div>

      <ReshipSection />
      <UnmappedSection />
      <DuplicatesSection />
    </div>
  );
}
